import { Icon } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect, useRef } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import {
  useOrderedDragDrop,
  type UseOrderedDragDropOptions,
} from 'utils/useOrderedDragDrop';

import { SidebarCardId } from '../../conversationSidebar.types';
import { useCardSectionVisibility } from '../../hooks/useCardSectionVisibility';
import { useFlatCardContext } from '../../hooks/useFlatCardContext/useFlatCardContext';
import { useSidebarMode } from '../../hooks/useSidebarMode';

import styles from './cardMoveHandle.module.scss';

type CardMoveHandleProps = { visibilityCardId?: SidebarCardId } & Pick<
  UseOrderedDragDropOptions,
  'type' | 'index' | 'onMove' | 'target'
>;

export const CardMoveHandle = ({
  index,
  type,
  onMove,
  target,
  visibilityCardId,
}: CardMoveHandleProps): JSX.Element | null => {
  const flatCard = useFlatCardContext();
  const { hiddenItems } = useCardSectionVisibility();
  // On the flat path hidden cards stay reorderable (matching the admin settings
  // page, where "always hide" rows are still draggable); they're only dimmed by
  // the surrounding wrapper. Only the legacy per-section path locks hidden items.
  const isDisabled = flatCard
    ? false
    : visibilityCardId
      ? hiddenItems.has(visibilityCardId)
      : false;
  const isDisabledRef = useRef(isDisabled);
  const rowHandle = useRef<HTMLDivElement>(null);
  const flags = useFeatureFlag(['conversation-right-sidebar-visibility']);
  isDisabledRef.current = isDisabled;
  const { isOver } = useOrderedDragDrop({
    type,
    index,
    source: rowHandle,
    target,
    preview: target,
    onMove,
    triggerSwap: 'drop',
    canDrag: () => !isDisabledRef.current,
  });

  useEffect(() => {
    target.current?.classList.toggle(styles.isOver, isOver);
  }, [isOver, target]);

  if (useSidebarMode() !== 'customization') {
    return null;
  }

  // On the flat path the presence of the context implies the feature is on;
  // the legacy path still gates on the old visibility flag.
  if (!flatCard && !flags.conversationRightSidebarVisibility) {
    return null;
  }

  return (
    <div
      ref={rowHandle}
      className={classnames(styles.handle, { [styles.disabled]: isDisabled })}
    >
      <Icon name="bars" />
    </div>
  );
};
